import Typography from "@mui/material/Typography";
import { toRelativeUrl } from "@okta/okta-auth-js";
import { useOktaAuth } from "@okta/okta-react";
import { useEffect, useState } from "react";
import type { JSX, ReactElement } from "react";

import { getExternalIdpForCurrentLocation } from "@/auth/idpRouting";
import { SprengnetterPage } from "@/components/sprengnetterPage";
import { authorizationService } from "@/hooks/permissionsHooks/authorizationService";
import { usePermissions } from "@/hooks/permissionsHooks/usePermissions";
import { ErrorPage } from "@/pages";
import { setUserInfo } from "@/pages/applicationMainSlice";
import { useAppDispatch } from "@/store/hooks";
import { RoleType } from "@/types/RoleType";

interface SecureRouteProps {
  element: ReactElement;
  path?: string;
  requiresAdmin?: boolean;
  requiresInstitutionAdmin?: boolean;
  exact?: boolean;
  sensitive?: boolean;
  strict?: boolean;
}

export const SecureRoute = ({ element, requiresAdmin, requiresInstitutionAdmin }: SecureRouteProps): JSX.Element => {
  const { oktaAuth, authState } = useOktaAuth();
  const permissions = usePermissions();
  const dispatch = useAppDispatch();
  const [loginError, setLoginError] = useState(false);

  useEffect(() => {
    let isActive = true;

    const handleRedirect = async (): Promise<void> => {
      if (!authState) {
        return;
      }

      setLoginError(false);

      if (!authState.isAuthenticated) {
        dispatch(setUserInfo(undefined));

        try {
          const idp = getExternalIdpForCurrentLocation();
          const originalUri = toRelativeUrl(window.location.href, window.location.origin);

          await oktaAuth.signInWithRedirect({ originalUri, ...(idp ? { idp } : {}) });
        } catch (error) {
          console.error("Unable to start login:", error);
          if (isActive) setLoginError(true);
        }

        return;
      }

      const info = await oktaAuth.getUser();

      if (!isActive) {
        return;
      }

      dispatch(
        setUserInfo({
          user: {
            name:
              info.given_name && info.family_name
                ? `${info.given_name} ${info.family_name}`.trim()
                : info.name || info.email || "",
            email: info.email,
          },
          company: {
            name: typeof info.organization === "string" ? info.organization : undefined,
          },
        }),
      );
    };

    void handleRedirect();

    return (): void => {
      isActive = false;
    };
  }, [oktaAuth, authState, dispatch]);

  if (loginError && authState && !authState.isAuthenticated) {
    return <ErrorPage />;
  }

  if (!authState || !authState?.isAuthenticated) {
    return <div />;
  }

  if (requiresAdmin && !authorizationService.isAdmin(authState?.accessToken?.accessToken)) {
    return (
      <SprengnetterPage>
        <Typography fontWeight="bold" variant="h3">
          Access Denied
        </Typography>
      </SprengnetterPage>
    );
  }

  if (requiresInstitutionAdmin && !permissions.has(RoleType.INSTITUTION_ADMIN)) {
    return (
      <SprengnetterPage>
        <Typography fontWeight="bold" variant="h3">
          Access Denied
        </Typography>
      </SprengnetterPage>
    );
  }

  return element;
};

const SecureRouteWrapper = ({ element, ...rest }: SecureRouteProps): JSX.Element => {
  return <SecureRoute {...rest} element={element} />;
};

export default SecureRouteWrapper;
